import { useEffect, useState } from 'react'
import { AlertTriangle, BellRing, Clock, LayoutDashboard } from 'lucide-react'
import { getMonitoringRisk } from '~/api/monitoring'
import type {
    AdminDashboard,
    MonitoringRiskResponse,
    MonitoringRiskRow,
} from '~/api/types'
import { StatCard } from '~/components/StatCard'
import { Badge } from '~/components/ui/badge'
import { Card } from '~/components/ui/card'
import { EmptyState } from '~/components/ui/empty-state'
import { Skeleton } from '~/components/ui/skeleton'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '~/components/ui/table'
import { cn, formatDateTime, whoLabel } from '~/lib/utils'

// 위험 관제 패널. 위험순으로 정렬된 대시보드를 KPI + 표로 보여주고,
// 행 클릭 시 기존 상세 흐름(selected → DashboardDetailPanel)으로 넘긴다.

// 위험 행 → 상세 패널이 기대하는 AdminDashboard 형태로 변환(식별/누가만 채운다).
function toDashboard(row: MonitoringRiskRow): AdminDashboard {
    return {
        id: row.dashboardId,
        ownerId: null,
        ownerName: row.ownerName,
        ownerEmail: row.ownerEmail,
        name: row.name,
        assessmentId: row.assessmentId,
        notifyEmail: null,
        createdAt: null,
    }
}

/** 이탈/근접/주의 배지 묶음. 모두 0이면 '—'. */
function SeverityBadges({ row }: { row: MonitoringRiskRow }) {
    if (!row.openHigh && !row.openMedium && !row.openLow)
        return <span className='text-muted-foreground'>—</span>
    return (
        <div className='flex flex-wrap gap-1'>
            {row.openHigh > 0 ? (
                <Badge tone='danger'>이탈 {row.openHigh}</Badge>
            ) : null}
            {row.openMedium > 0 ? (
                <Badge tone='warning'>근접 {row.openMedium}</Badge>
            ) : null}
            {row.openLow > 0 ? (
                <Badge tone='neutral'>주의 {row.openLow}</Badge>
            ) : null}
        </div>
    )
}

export function MonitoringRiskPanel({
    onSelect,
    selectedId,
}: {
    onSelect?: (dashboard: AdminDashboard) => void
    selectedId?: string
}) {
    const [data, setData] = useState<MonitoringRiskResponse | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        let alive = true
        getMonitoringRisk()
            .then((d) => alive && setData(d))
            .catch((e) => alive && setError(e.message))
            .finally(() => alive && setLoading(false))
        return () => {
            alive = false
        }
    }, [])

    const summary = data?.summary
    const rows = data?.rows ?? []

    return (
        <div>
            {error ? <p className='text-destructive mb-4'>{error}</p> : null}

            <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
                <StatCard
                    label='대시보드'
                    value={summary?.dashboards ?? '—'}
                    icon={LayoutDashboard}
                    tone='primary'
                />
                <StatCard
                    label='위험 대시보드'
                    value={summary?.atRisk ?? '—'}
                    icon={AlertTriangle}
                    tone='warning'
                />
                <StatCard
                    label='미해결 high'
                    value={summary?.openHigh ?? '—'}
                    icon={BellRing}
                    tone='danger'
                />
                <StatCard
                    label='방치'
                    value={summary?.stale ?? '—'}
                    icon={Clock}
                    tone='warning'
                />
            </div>

            <Card className='mt-6 p-5'>
                <h3 className='krds-title text-foreground mb-3'>위험 관제</h3>
                {!loading && rows.length === 0 ? (
                    <EmptyState
                        icon={LayoutDashboard}
                        title='위험 대시보드가 없어요.'
                        description='임계치를 벗어났거나 방치된 대시보드가 생기면 여기에 위험순으로 표시돼요.'
                    />
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>대시보드</TableHead>
                                <TableHead>사용자</TableHead>
                                <TableHead>이탈/근접</TableHead>
                                <TableHead>미확인</TableHead>
                                <TableHead>마지막 측정</TableHead>
                                <TableHead>상태</TableHead>
                                <TableHead>측정수</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading
                                ? Array.from({ length: 5 }).map((_, i) => (
                                      <TableRow key={i}>
                                          {Array.from({ length: 7 }).map(
                                              (_, j) => (
                                                  <TableCell key={j}>
                                                      <Skeleton className='h-4 w-16' />
                                                  </TableCell>
                                              )
                                          )}
                                      </TableRow>
                                  ))
                                : rows.map((row) => (
                                      <TableRow
                                          key={row.dashboardId}
                                          onClick={() =>
                                              onSelect?.(toDashboard(row))
                                          }
                                          className={cn(
                                              'cursor-pointer',
                                              row.dashboardId === selectedId &&
                                                  'bg-accent hover:bg-accent'
                                          )}>
                                          <TableCell className='font-bold'>
                                              {row.name}
                                          </TableCell>
                                          <TableCell
                                              className='max-w-[180px] truncate'
                                              title={
                                                  row.ownerEmail ?? undefined
                                              }>
                                              {whoLabel(
                                                  row.ownerName,
                                                  row.ownerEmail
                                              )}
                                          </TableCell>
                                          <TableCell>
                                              <SeverityBadges row={row} />
                                          </TableCell>
                                          <TableCell className='tnum'>
                                              {row.unread}
                                          </TableCell>
                                          <TableCell className='whitespace-nowrap'>
                                              {row.lastReadingAt ? (
                                                  formatDateTime(
                                                      row.lastReadingAt
                                                  )
                                              ) : (
                                                  <span className='text-muted-foreground'>
                                                      측정 없음
                                                  </span>
                                              )}
                                          </TableCell>
                                          <TableCell>
                                              {row.stale ? (
                                                  <Badge tone='warning'>
                                                      방치
                                                  </Badge>
                                              ) : (
                                                  <span className='text-muted-foreground'>
                                                      —
                                                  </span>
                                              )}
                                          </TableCell>
                                          <TableCell className='tnum'>
                                              {row.readingsCount}
                                          </TableCell>
                                      </TableRow>
                                  ))}
                        </TableBody>
                    </Table>
                )}
            </Card>
        </div>
    )
}
